import * as THREE from 'three';
import { ARENA_RADIUS, LANE_HALF_WIDTH, LANE_PATHS, NEXUS_Z, SHOP } from '@moba/shared';
import { makeGlowTexture } from './assets.ts';

export interface Arena {
  group: THREE.Group;
  update(dt: number): void;
  /** Muestra sólo el empedrado de esas líneas (ARAM: la central). */
  setActiveLanes(lanes: number[]): void;
}

export function createArena(scene: THREE.Scene): Arena {
  const group = new THREE.Group();
  group.name = 'arena';
  scene.add(group);

  scene.background = new THREE.Color(0x0a120c);
  scene.fog = new THREE.FogExp2(0x0a120c, 0.007);

  group.add(makeGround());
  group.add(makeRings());
  const lanes = makeLanes();
  group.add(lanes);
  group.add(makePillars());
  group.add(makeSpawnGlow());
  group.add(makeShops());
  group.add(makeLights());

  const dust = makeDust();
  group.add(dust.points);

  return {
    group,
    setActiveLanes(active: number[]): void {
      for (const child of lanes.children) child.visible = active.includes(child.userData.lane as number);
    },
    update(dt: number): void {
      group.traverse((o) => {
        if (o.name === 'shop-coin') o.rotation.z += dt * 2.4;
      });
      dust.points.rotation.y += dt * 0.012;
      dust.points.position.y = Math.sin(performance.now() * 0.0002) * 0.15;
    },
  };
}

/** Tamaño en unidades del mundo que cubre una repetición de cada textura del suelo. */
const GRASS_TILE = 7;
const STONE_TILE = 3.2;

/** Dibuja `draw(x, y)` también desplazado ±size para que la textura repita sin costuras. */
function drawWrapped(size: number, x: number, y: number, reach: number, draw: (x: number, y: number) => void): void {
  for (const dx of [-size, 0, size]) {
    for (const dy of [-size, 0, size]) {
      const px = x + dx;
      const py = y + dy;
      if (px < -reach || px > size + reach || py < -reach || py > size + reach) continue;
      draw(px, py);
    }
  }
}

function canvasTexture(canvas: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 8;
  return texture;
}

function makeCanvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D no disponible');
  return [canvas, ctx];
}

/** Pasto: base verde con manchas suaves y miles de briznas de distintos tonos. */
function makeGrassTexture(): THREE.CanvasTexture {
  const size = 512;
  const [canvas, ctx] = makeCanvas(size);
  ctx.fillStyle = '#3d7a2e';
  ctx.fillRect(0, 0, size, size);

  // manchas grandes de tono para romper la repetición
  for (let i = 0; i < 40; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 30 + Math.random() * 70;
    const light = Math.random() < 0.5;
    drawWrapped(size, x, y, r, (px, py) => {
      const g = ctx.createRadialGradient(px, py, 0, px, py, r);
      g.addColorStop(0, light ? 'rgba(120, 170, 70, 0.28)' : 'rgba(25, 70, 25, 0.3)');
      g.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(px - r, py - r, r * 2, r * 2);
    });
  }

  // briznas
  ctx.lineCap = 'round';
  for (let i = 0; i < 9000; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const len = 3 + Math.random() * 7;
    const lean = (Math.random() - 0.5) * 4;
    const h = 85 + Math.random() * 35;
    const s = 40 + Math.random() * 25;
    const l = 22 + Math.random() * 26;
    ctx.strokeStyle = `hsla(${h}, ${s}%, ${l}%, 0.85)`;
    ctx.lineWidth = 1 + Math.random() * 0.8;
    drawWrapped(size, x, y, 12, (px, py) => {
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + lean, py - len);
      ctx.stroke();
    });
  }

  // florecillas y tréboles sueltos
  for (let i = 0; i < 26; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    ctx.fillStyle = Math.random() < 0.5 ? 'rgba(240, 236, 200, 0.8)' : 'rgba(250, 210, 90, 0.8)';
    drawWrapped(size, x, y, 3, (px, py) => {
      ctx.beginPath();
      ctx.arc(px, py, 1.6, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  return canvasTexture(canvas, true);
}

/** Adoquines en hileras trabadas, con juntas oscuras. Devuelve color y mapa de relieve. */
function makeStoneTextures(): { map: THREE.CanvasTexture; bump: THREE.CanvasTexture } {
  const size = 512;
  const [canvas, ctx] = makeCanvas(size);
  const [bumpCanvas, bumpCtx] = makeCanvas(size);
  ctx.fillStyle = '#3b3a36';
  ctx.fillRect(0, 0, size, size);
  bumpCtx.fillStyle = '#000';
  bumpCtx.fillRect(0, 0, size, size);

  const rows = 6;
  const cols = 5;
  const cellH = size / rows;
  const cellW = size / cols;
  const gap = 5;
  for (let row = 0; row < rows; row++) {
    const offset = row % 2 === 0 ? 0 : cellW / 2;
    for (let col = 0; col < cols; col++) {
      const x = col * cellW + offset + gap / 2 + (Math.random() - 0.5) * 3;
      const y = row * cellH + gap / 2 + (Math.random() - 0.5) * 3;
      const w = cellW - gap + (Math.random() - 0.5) * 4;
      const hgt = cellH - gap + (Math.random() - 0.5) * 4;
      const base = 112 + Math.random() * 46;
      const warm = Math.random() * 12;
      const fill = `rgb(${base + warm}, ${base + warm * 0.6}, ${base - 4})`;
      drawWrapped(size, x, y, cellW, (px, py) => {
        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.roundRect(px, py, w, hgt, 12);
        ctx.fill();
        // luz arriba, sombra abajo: da volumen al adoquín
        const shade = ctx.createLinearGradient(px, py, px, py + hgt);
        shade.addColorStop(0, 'rgba(255, 255, 255, 0.12)');
        shade.addColorStop(1, 'rgba(0, 0, 0, 0.22)');
        ctx.fillStyle = shade;
        ctx.fill();
        bumpCtx.fillStyle = '#fff';
        bumpCtx.beginPath();
        bumpCtx.roundRect(px, py, w, hgt, 12);
        bumpCtx.fill();
      });
    }
  }

  // desgaste: motas claras y oscuras
  for (let i = 0; i < 5000; i++) {
    const dark = Math.random() < 0.55;
    ctx.fillStyle = dark ? `rgba(30, 28, 24, ${Math.random() * 0.25})` : `rgba(230, 225, 210, ${Math.random() * 0.18})`;
    ctx.fillRect(Math.random() * size, Math.random() * size, 1.5, 1.5);
  }
  // musgo en algunas juntas
  for (let i = 0; i < 70; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    drawWrapped(size, x, y, 8, (px, py) => {
      ctx.fillStyle = 'rgba(70, 110, 45, 0.35)';
      ctx.beginPath();
      ctx.arc(px, py, 2 + Math.random() * 4, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  return { map: canvasTexture(canvas, true), bump: canvasTexture(bumpCanvas, false) };
}

let grassCache: THREE.CanvasTexture | undefined;
function grassTexture(): THREE.CanvasTexture {
  grassCache ??= makeGrassTexture();
  return grassCache;
}

let stoneCache: THREE.MeshStandardMaterial | undefined;
/** Material compartido de los carriles y el círculo central. */
function stoneMaterial(): THREE.MeshStandardMaterial {
  if (!stoneCache) {
    const { map, bump } = makeStoneTextures();
    stoneCache = new THREE.MeshStandardMaterial({ map, bumpMap: bump, bumpScale: 1.5, roughness: 0.9, metalness: 0 });
  }
  return stoneCache;
}

/**
 * Pone UVs en coordenadas del mundo (x, z) / tile a una geometría ya colocada en el plano XZ:
 * las piezas que se solapan (tramos y codos de los carriles) muestran el mismo dibujo y no parpadean.
 */
function worldUv(geometry: THREE.BufferGeometry, tile: number): THREE.BufferGeometry {
  const pos = geometry.getAttribute('position');
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = pos.getX(i) / tile;
    uv[i * 2 + 1] = pos.getZ(i) / tile;
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geometry;
}

function makeGround(): THREE.Mesh {
  const geometry = new THREE.PlaneGeometry(140, 140);
  geometry.rotateX(-Math.PI / 2);
  const material = new THREE.MeshStandardMaterial({
    map: grassTexture(),
    // fuera de la arena el pasto es más oscuro y se pierde en la niebla
    color: 0x6f8a62,
    roughness: 1,
    metalness: 0,
  });
  const mesh = new THREE.Mesh(worldUv(geometry, GRASS_TILE), material);
  mesh.receiveShadow = true;
  return mesh;
}

function makeRings(): THREE.Group {
  const group = new THREE.Group();

  const floorGeometry = new THREE.CircleGeometry(ARENA_RADIUS - 0.2, 96);
  floorGeometry.rotateX(-Math.PI / 2);
  const floor = new THREE.Mesh(
    worldUv(floorGeometry, GRASS_TILE),
    new THREE.MeshStandardMaterial({ map: grassTexture(), color: 0xffffff, roughness: 1, metalness: 0 }),
  );
  floor.position.y = 0.01;
  floor.receiveShadow = true;
  group.add(floor);

  const outer = new THREE.Mesh(
    new THREE.RingGeometry(ARENA_RADIUS - 0.35, ARENA_RADIUS, 128),
    new THREE.MeshBasicMaterial({
      color: 0x4d9be0,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  outer.rotation.x = -Math.PI / 2;
  outer.position.y = 0.03;
  group.add(outer);

  const glow = new THREE.Mesh(
    new THREE.RingGeometry(ARENA_RADIUS - 1.6, ARENA_RADIUS - 0.2, 128),
    new THREE.MeshBasicMaterial({
      color: 0x2f6ea8,
      transparent: true,
      opacity: 0.12,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.025;
  group.add(glow);

  const centerGeometry = new THREE.CircleGeometry(3.6, 64);
  centerGeometry.rotateX(-Math.PI / 2);
  const center = new THREE.Mesh(worldUv(centerGeometry, STONE_TILE), stoneMaterial());
  center.position.y = 0.02;
  center.receiveShadow = true;
  group.add(center);

  const centerRing = new THREE.Mesh(
    new THREE.RingGeometry(3.6, 3.78, 64),
    new THREE.MeshBasicMaterial({
      color: 0x6fb6ff,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  centerRing.rotation.x = -Math.PI / 2;
  centerRing.position.y = 0.035;
  group.add(centerRing);

  const innerRing = new THREE.Mesh(
    new THREE.RingGeometry(1.2, 1.28, 48),
    new THREE.MeshBasicMaterial({
      color: 0x8fd0ff,
      transparent: true,
      opacity: 0.28,
      side: THREE.DoubleSide, forceSinglePass: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  innerRing.rotation.x = -Math.PI / 2;
  innerRing.position.y = 0.035;
  group.add(innerRing);

  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const tick = new THREE.Mesh(
      new THREE.PlaneGeometry(0.18, 1.2),
      new THREE.MeshBasicMaterial({
        color: 0x5fa8e8,
        transparent: true,
        opacity: 0.4,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide, forceSinglePass: true,
      }),
    );
    tick.rotation.x = -Math.PI / 2;
    tick.rotation.z = -angle;
    tick.position.set(Math.sin(angle) * 2.5, 0.04, Math.cos(angle) * 2.5);
    group.add(tick);
  }

  return group;
}

/** Franja plana en XZ de `a` a `b` con media anchura `half`, a la altura `y`. */
function laneStrip(a: { x: number; z: number }, b: { x: number; z: number }, half: number, y: number): THREE.BufferGeometry {
  const length = Math.hypot(b.x - a.x, b.z - a.z);
  const geometry = new THREE.PlaneGeometry(half * 2, length);
  geometry.rotateX(-Math.PI / 2);
  geometry.rotateY(Math.atan2(b.x - a.x, b.z - a.z));
  geometry.translate((a.x + b.x) / 2, y, (a.z + b.z) / 2);
  return geometry;
}

function laneDisc(p: { x: number; z: number }, radius: number, y: number): THREE.BufferGeometry {
  const geometry = new THREE.CircleGeometry(radius, 32);
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(p.x, y, p.z);
  return geometry;
}

/** Carriles empedrados con un bordillo de piedra oscura; los codos se tapan con discos. */
function makeLanes(): THREE.Group {
  const group = new THREE.Group();
  const stone = stoneMaterial();
  const curb = new THREE.MeshStandardMaterial({ color: 0x4a4740, roughness: 0.95, metalness: 0 });
  const curbWidth = 0.3;

  for (const [laneIndex, path] of LANE_PATHS.entries()) {
    const laneGroup = new THREE.Group();
    laneGroup.userData.lane = laneIndex;
    group.add(laneGroup);
    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1];
      const b = path[i];
      const border = new THREE.Mesh(laneStrip(a, b, LANE_HALF_WIDTH + curbWidth, 0.013), curb);
      const lane = new THREE.Mesh(worldUv(laneStrip(a, b, LANE_HALF_WIDTH, 0.016), STONE_TILE), stone);
      border.receiveShadow = true;
      lane.receiveShadow = true;
      laneGroup.add(border, lane);
      if (i < path.length - 1) {
        const borderDisc = new THREE.Mesh(laneDisc(b, LANE_HALF_WIDTH + curbWidth, 0.0125), curb);
        const disc = new THREE.Mesh(worldUv(laneDisc(b, LANE_HALF_WIDTH, 0.0155), STONE_TILE), stone);
        borderDisc.receiveShadow = true;
        disc.receiveShadow = true;
        laneGroup.add(borderDisc, disc);
      }
    }
  }

  return group;
}

function makePillars(): THREE.Group {
  const group = new THREE.Group();
  const stone = new THREE.MeshStandardMaterial({ color: 0x1b2836, roughness: 0.82, metalness: 0.16 });
  const trim = new THREE.MeshStandardMaterial({ color: 0x27384a, roughness: 0.6, metalness: 0.3 });

  for (let i = 0; i < 16; i++) {
    const angle = (i / 16) * Math.PI * 2;
    const radius = ARENA_RADIUS + 1.6;
    const x = Math.sin(angle) * radius;
    const z = Math.cos(angle) * radius;

    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.68, 0.82, 0.34, 8), trim);
    base.position.set(x, 0.17, z);
    base.castShadow = true;
    base.receiveShadow = true;
    group.add(base);

    const column = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.46, 3.1, 8), stone);
    column.position.set(x, 1.85, z);
    column.castShadow = true;
    column.receiveShadow = true;
    group.add(column);

    const band = new THREE.Mesh(
      new THREE.CylinderGeometry(0.42, 0.42, 0.1, 8),
      new THREE.MeshBasicMaterial({
        color: i % 2 === 0 ? 0x4d9be0 : 0xe05a5a,
        transparent: true,
        opacity: 0.8,
      }),
    );
    band.position.set(x, 3.35, z);
    group.add(band);

    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.52, 0.36, 0.18, 8), trim);
    cap.position.set(x, 3.5, z);
    cap.castShadow = true;
    group.add(cap);
  }

  return group;
}

function makeSpawnGlow(): THREE.Group {
  const group = new THREE.Group();
  const texture = makeGlowTexture();
  const specs: Array<[number, number, number]> = [
    [0, -NEXUS_Z, 0x3f8fe0],
    [0, NEXUS_Z, 0xe05757],
  ];

  for (const [x, z, color] of specs) {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(18, 18),
      new THREE.MeshBasicMaterial({
        map: texture,
        color,
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(x, 0.05, z);
    group.add(mesh);
  }

  return group;
}

function makeLights(): THREE.Group {
  const group = new THREE.Group();

  const hemi = new THREE.HemisphereLight(0xcfe4ff, 0x2a3a1e, 1.1);
  group.add(hemi);

  const sun = new THREE.DirectionalLight(0xfff1dc, 2.1);
  sun.position.set(16, 28, -12);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -34;
  sun.shadow.camera.right = 34;
  sun.shadow.camera.top = 34;
  sun.shadow.camera.bottom = -34;
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 90;
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.02;
  group.add(sun);
  group.add(sun.target);

  const blue = new THREE.PointLight(0x4d9be0, 42, 42, 2);
  blue.position.set(-21, 5, -8);
  group.add(blue);

  const red = new THREE.PointLight(0xe05a5a, 42, 42, 2);
  red.position.set(21, 5, 8);
  group.add(red);

  return group;
}

function makeDust(): { points: THREE.Points } {
  const count = 280;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.sqrt(Math.random()) * (ARENA_RADIUS + 4);
    positions[i * 3] = Math.sin(angle) * radius;
    positions[i * 3 + 1] = Math.random() * 7 + 0.2;
    positions[i * 3 + 2] = Math.cos(angle) * radius;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const material = new THREE.PointsMaterial({
    color: 0xa8d4ff,
    size: 0.07,
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
  });

  return { points: new THREE.Points(geometry, material) };
}

/**
 * Tienda en cada base: mostrador con techo hexagonal dorado y una moneda que
 * flota, más el área de compra (radio SHOP.radius alrededor del nexo) en el suelo.
 */
function makeShops(): THREE.Group {
  const group = new THREE.Group();
  const wood = new THREE.MeshStandardMaterial({ color: 0x6b4a2a, roughness: 0.8 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0xd9a63a, roughness: 0.45, metalness: 0.3, emissive: 0x3a2400, emissiveIntensity: 0.6 });
  const coinMat = new THREE.MeshBasicMaterial({ color: 0xffd166 });
  const glow = makeGlowTexture();
  for (const side of [-1, 1]) {
    const shop = new THREE.Group();
    shop.position.set(-side * 7.5, 0, side * (NEXUS_Z + 4.5));
    const counter = new THREE.Mesh(new THREE.BoxGeometry(2.6, 1.0, 1.4), wood);
    counter.position.y = 0.5;
    counter.castShadow = true;
    shop.add(counter);
    for (const x of [-1.15, 1.15]) {
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 2.2, 6), wood);
      post.position.set(x, 1.1, 0);
      shop.add(post);
    }
    const roof = new THREE.Mesh(new THREE.ConeGeometry(1.9, 0.9, 6), roofMat);
    roof.position.y = 2.55;
    roof.castShadow = true;
    shop.add(roof);
    const coin = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.08, 24), coinMat);
    coin.rotation.x = Math.PI / 2;
    coin.position.y = 3.6;
    coin.name = 'shop-coin';
    shop.add(coin);
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: glow, color: 0xffc040, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false }));
    halo.position.y = 3.6;
    halo.scale.setScalar(2.4);
    shop.add(halo);
    group.add(shop);

    // área de compra alrededor del nexo propio
    const ringGeometry = new THREE.RingGeometry(SHOP.radius - 0.12, SHOP.radius, 96);
    ringGeometry.rotateX(-Math.PI / 2);
    const ring = new THREE.Mesh(ringGeometry, new THREE.MeshBasicMaterial({ color: 0xffd166, transparent: true, opacity: 0.22, depthWrite: false, side: THREE.DoubleSide, forceSinglePass: true }));
    ring.position.set(0, 0.03, side * NEXUS_Z);
    group.add(ring);
  }
  return group;
}
